import { useContext, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { StepWizardChildProps } from 'react-step-wizard';
import { useAsyncResource } from 'use-async-resource';
import { CustomDropdown } from '../components/Dashboard/CustomDropdown';
import { RequestContext } from '../context/RequestContext';
import { useGetUsers } from '../utils/users-hooks';
import { BranchesContext } from '../context/BranchesContext';
import { User } from '../models/table-models';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../utils/Enums/Roles';
import { useGetNewRequestClientDevices } from '../utils/clients-hooks';

export const DistributorSelect: React.FC<Partial<StepWizardChildProps>> = ({ nextStep }) => {
    const { taskDescription, setTaskDescription } = useContext(RequestContext);
    const { hasPermission, loggedUser } = useContext(AuthContext);
    const { selectedBranches } = useContext(BranchesContext);

    const [readDevices] = useAsyncResource(useGetNewRequestClientDevices, selectedBranches[0].id);
    const devices = readDevices()?.items;

    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [getDistributors] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const distributors = getDistributors();
    const requestTypes = ['INSTALLATION', 'MAINTENANCE', 'DEVICE_RETURN'];

    const setTaskDescriptionType = (entitie: string) => {
        setTaskDescription({ ...taskDescription, category: entitie });
    };

    const setTaskDescriptionAssignee = (entitie: string) => {
        const currentDistributor = distributors.items.find((distributor) => distributor?.id?.toString() === entitie);

        setTaskDescription({
            ...taskDescription,
            assigneeId: +entitie,
            assigneeName: distributorLabel(currentDistributor),
        });
    };

    const distributorLabel = (distributor?: User) => {
        return distributor?.group == null
            ? `${distributor?.name}`
            : `${distributor?.name} ${distributor?.group.mainGroup}-${distributor?.group.subGroup}`;
    };
    const isDistributor = !hasPermission([Role.COORDINATOR]) && hasPermission([Role.DISTRIBUTOR]);
    const defaultDistributor = isDistributor
        ? loggedUser
        : distributors.items.find(({ id }) => selectedBranches[0].assignedDistributor === id);

    const defaultType = devices?.length == 0 ? 'INSTALLATION' : 'MAINTENANCE';

    useEffect(() => {
        const initialParams = {
            assigneeId: defaultDistributor?.id ?? 4,
            category: 'INSTALLATION',
            assigneeName: distributorLabel(defaultDistributor as User),
        };

        if (devices?.length > 0) {
            initialParams.category = 'MAINTENANCE';
        }

        if (defaultDistributor) setTaskDescriptionAssignee(String(defaultDistributor.id));
        setTaskDescription({ ...taskDescription, ...initialParams });
    }, []);

    return (
        <div className=' wizard-content d-flex flex-column h-100'>
            {!isDistributor && (
                <CustomDropdown
                    entities={distributors.items.map((distributor) => {
                        return { label: distributorLabel(distributor), value: `${distributor?.id}` };
                    })}
                    disabled={isDistributor}
                    setEntities={setTaskDescriptionAssignee}
                    label={t('distributor')}
                    defaultValue={defaultDistributor?.id}
                />
            )}

            <CustomDropdown
                disabled={selectedBranches.length > 1 || devices?.length == 0}
                entities={requestTypes.map((request) => {
                    return { label: t(request), value: request };
                })}
                setEntities={setTaskDescriptionType}
                label={t('taskType')}
                defaultValue={defaultType}
            />
            <div className='d-flex wizard-buttons w-100 justify-content-end'>
                <Button onClick={() => nextStep!()}>{t('next')}</Button>
            </div>
        </div>
    );
};
